/**
 * Marketplace API
 *
 * RTK Query endpoints for marketplace search, listening, and hire requests.
 */

import { api } from '../api';

// Types
export type ListenType = 'preview' | 'full';
export type HireRequestStatus = 'pending' | 'accepted' | 'rejected' | 'closed';

export interface FeaturedClipSummary {
  id: string;
  title: string;
  clipType: 'assessment' | 'practice' | 'coaching';
  durationSeconds?: number;
}

/**
 * MarketplaceProfile as returned by GET /marketplace/profiles search.
 * This is a flat shape (MarketplaceProfileWithDetails from the backend),
 * NOT the raw MarketplaceProfile DB model.
 */
export interface MarketplaceProfile {
  userId: string;
  displayName: string | null;
  headline: string | null;
  primaryRole: string | null;
  primarySpecialty: string | null;
  overallScore: number | null;
  /** Part of the authority section: null when the person hides it. */
  sessionCount: number | null;
  rateType: string;
  rateAmount: number | null;
  currency: string;
  location: string | null;
  badges: string[];
  verifiedRoles: string[];
  verifiedSpecialties: string[];
  authorityLevel?: string;
  authorityScoreAvg?: number;
  authorityRankScore?: number;
  domainCertsCount?: number;
  personasMasteredCount?: number;
  npcLevelsBeaten?: number;
  publicSlug?: string | null;
  /** The published portfolio's id — what a hire request (Contact) names. */
  portfolioId?: string;
  availableForHire?: boolean;
  featuredClipsSummaryJson?: FeaturedClipSummary[];
}

export interface MarketplaceSearchParams {
  roleKeys?: string;
  specialtyKeys?: string;
  minRate?: number;
  maxRate?: number;
  location?: string;
  query?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'popularityScore' | 'overallScore' | 'sessionCount' | 'rateAmount' | 'rankingScore';
  sortOrder?: 'ASC' | 'DESC';
  clipType?: 'assessment' | 'practice' | 'coaching';
}

export interface MarketplaceSearchResponse {
  profiles: MarketplaceProfile[];
  total: number;
  limit: number;
  offset: number;
}

export interface RecordListenRequest {
  targetUserId: string;
  highlightId?: string;
  portfolioId?: string;
  listenType: ListenType;
  durationSeconds?: number;
}

export interface ListenEvent {
  id: string;
  listenerUserId: string;
  targetUserId: string;
  highlightId?: string;
  portfolioId?: string;
  listenType: ListenType;
  creditsConsumed: number;
  durationSeconds?: number;
  createdAt: string;
}

export interface ListenStats {
  totalListens: number;
  previewListens: number;
  fullListens: number;
  uniqueListeners: number;
  recentListens: ListenEvent[];
}

export interface HireRequest {
  id: string;
  requesterUserId: string;
  targetUserId: string;
  portfolioId: string;
  message: string;
  context?: string;
  status: HireRequestStatus;
  createdAt: string;
  updatedAt: string;
  // Joined data
  requester?: {
    id: string;
    email: string;
  };
  target?: {
    id: string;
    email: string;
  };
  portfolio?: {
    id: string;
    displayName?: string;
  };
}

export interface CreateHireRequestRequest {
  targetUserId: string;
  portfolioId: string;
  message: string;
  context?: string;
}

export interface HireRequestListParams {
  status?: HireRequestStatus;
  limit?: number;
  offset?: number;
}

export interface HireRequestListResponse {
  requests: HireRequest[];
  total: number;
}

export interface HireRequestStats {
  received: {
    total: number;
    pending: number;
    accepted: number;
    rejected: number;
    closed: number;
  };
  sent: {
    total: number;
    pending: number;
    accepted: number;
    rejected: number;
    closed: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const marketplaceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================
    // Marketplace Search & Profiles
    // =====================================================

    // Search marketplace profiles
    searchMarketplaceProfiles: builder.query<MarketplaceSearchResponse, MarketplaceSearchParams>({
      query: (params) => ({
        url: '/marketplace/profiles',
        params,
      }),
      transformResponse: (response: ApiResponse<MarketplaceProfile[]> & { meta?: { total?: number; limit?: number; offset?: number } }) => ({
        profiles: response.data,
        total: response.meta?.total ?? 0,
        limit: response.meta?.limit ?? 20,
        offset: response.meta?.offset ?? 0,
      }),
      providesTags: ['Marketplace'],
    }),

    // =====================================================
    // Listen Events
    // =====================================================

    // Record a listen event
    recordListen: builder.mutation<ListenEvent, RecordListenRequest>({
      query: (body) => ({
        url: '/marketplace/listen',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<ListenEvent>) => response.data,
      invalidatesTags: ['ListenStats'],
    }),

    // =====================================================
    // Hire Requests
    // =====================================================

    // Create a hire request
    createHireRequest: builder.mutation<HireRequest, CreateHireRequestRequest>({
      query: (body) => ({
        url: '/marketplace/hire-requests',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<HireRequest>) => response.data,
      invalidatesTags: ['HireRequests'],
    }),

    // Get hire requests received by current user
    getMyHireRequests: builder.query<HireRequestListResponse, HireRequestListParams>({
      query: (params) => ({
        url: '/marketplace/hire-requests/me',
        params,
      }),
      transformResponse: (response: ApiResponse<HireRequestListResponse>) => response.data,
      providesTags: ['HireRequests'],
    }),

    // Get hire requests sent by current user
    getMySentHireRequests: builder.query<HireRequestListResponse, HireRequestListParams>({
      query: (params) => ({
        url: '/marketplace/hire-requests/sent',
        params,
      }),
      transformResponse: (response: ApiResponse<HireRequestListResponse>) => response.data,
      providesTags: ['HireRequests'],
    }),

    // Get hire request stats
    getHireRequestStats: builder.query<HireRequestStats, void>({
      query: () => '/marketplace/hire-requests/stats',
      transformResponse: (response: ApiResponse<HireRequestStats>) => response.data,
      providesTags: ['HireRequests'],
    }),

    // Update hire request status
    updateHireRequestStatus: builder.mutation<HireRequest, { id: string; status: HireRequestStatus }>({
      query: ({ id, status }) => ({
        url: `/marketplace/hire-requests/${id}`,
        method: 'PATCH',
        body: { status },
      }),
      transformResponse: (response: ApiResponse<HireRequest>) => response.data,
      invalidatesTags: (_result, _error, { id }) => ['HireRequests', { type: 'HireRequests', id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  // Marketplace
  useSearchMarketplaceProfilesQuery,
  // Listen
  useRecordListenMutation,
  // Hire Requests
  useCreateHireRequestMutation,
  useGetMyHireRequestsQuery,
  useGetMySentHireRequestsQuery,
  useGetHireRequestStatsQuery,
  useUpdateHireRequestStatusMutation,
} = marketplaceApi;
